'use client';

import { c as _c } from "react/compiler-runtime";
import { useSession } from '@/lib/auth-client';
import { useSiteSettings } from '@/lib/hooks/useSiteSettings';
import { cn } from '@/lib/utils';
import type { SiteId } from '@sizzlenz/shared';
import { ArrowUpRight } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { AudienceSplitSection, ExpertPartnersSection, ExploreToolsSection, InspirationFeedSection, OfficeHoursSection, HomeCollaborationsSection, SizzleConciergeSection } from '.';
import { FinalCTASectionV3 } from '../HomeV3';
import { SignInEmailBanner } from '../auth/SignInEmailBanner';
import { HeroSectionSizzleCPG } from './HeroSectionSizzleCPG';
import { HeroSectionSizzleNZ } from './HeroSectionSizzleNZ';
import { LazySection } from './LazySection';
function V1GoalsCallout() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="mx-auto mb-3 max-w-2xl text-sm font-semibold uppercase tracking-wide text-primary-600 sm:text-base">The goals and our measure of success with Sizzle are simple:</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-primary-600">get inspired</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-primary-600">make your next moves</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="container mx-auto px-4 sm:px-6 lg:px-8"><div className="mx-auto max-w-3xl rounded-3xl border border-orange-100 bg-white px-8 py-12 text-center shadow-sm ring-1 ring-orange-500/5 sm:px-14 sm:py-14">{t0}<p className="mx-auto max-w-2xl text-2xl font-extrabold leading-snug text-gray-900 sm:text-3xl">You {t1} and{" "}{t2} that{" "}<span className="text-primary-600">grow your business</span>.</p><p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg">Sizzle and our network of expert partners make that easier, so you've got no more excuses for not making your next move.</p></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function BusinessMarketplaceHeading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="container mx-auto px-4 py-8 text-center sm:px-6 lg:px-8"><h2 className="mx-auto max-w-3xl text-xl font-extrabold leading-tight text-gray-900">Sizzle is built for brands, trade businesses, agencies, providers, and hosts to be inspired, discover, and connect with opportunities.</h2></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface HomeSection {
  key: string;
  content: ReactNode;
  /**
   * Explicit background override. Only set this for sections that need a fixed
   * color (e.g. dark banners). When omitted, the section receives an automatic
   * alternating background derived from its position in the visible list, keeping
   * the light/off-white rhythm consistent regardless of which sections are toggled on.
   */
  background?: string;
  isSignInBanner?: boolean;
  /** If true, section is lazy-loaded with a skeleton placeholder. */
  lazy?: boolean;
  /** Space reserved while a lazy section approaches the viewport. */
  lazyMinHeight?: string;
  /** If set, section only renders on the listed domains. Omit to show on all domains. */
  sites?: SiteId[];
  /**
   * Vertical spacing applied below the section as a Tailwind class (e.g. `pb-16`,
   * `mb-24`). Defaults to `DEFAULT_SECTION_SPACING` when omitted.
   */
  spacing?: string;
}

/** Default vertical gap rendered between home sections when none is specified. */
const DEFAULT_SECTION_SPACING = 'pb-16 sm:pb-20 lg:pb-24';

/**
 * Two-tone background rhythm used to alternate sections. Index 0 is applied to the
 * first visible section, index 1 to the next, and so on (wrapping). Sections that
 * set an explicit `background` opt out of this alternation.
 */
const ALTERNATING_BACKGROUNDS = ['bg-white', 'bg-[#fffaf4]'] as const;
const SECTIONS: HomeSection[] = [{
  key: 'hero-nz',
  content: <HeroSectionSizzleNZ />,
  sites: ['sizzlenz'],
  // The hero is a self-contained banner with its own internal padding, so it
  // needs no extra gap below it — the next band should butt up directly
  // against it rather than leaving a large empty white strip.
  spacing: 'pb-0'
},
// {
//   key: 'v1-goals-callout',
//   content: <V1GoalsCallout />,
//   background: 'bg-[#fffaf4]',
//   spacing: 'pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-24 lg:pb-24',
// },

{
  key: 'hero-cpg',
  content: <HeroSectionSizzleCPG />,
  sites: ['sizzlecpg'],
  spacing: 'pb-0'
}, {
  key: 'audience-split',
  content: <AudienceSplitSection />,
  sites: ['sizzlenz'],
  // Self-contained banner with its own cream background + internal padding and a
  // bottom border, so it butts directly against the hero above and the next band
  // below without an extra spacing gap.
  spacing: 'pb-0'
},
// {
//   key: 'demand-to-business',
//   content: <DemandToBusinessSection />,
//   background: 'bg-white',
//   sites: ['sizzlenz'],
// },
// {
//   key: 'demand',
//   content: <DemandTeaser />,
//   background: 'bg-gray-950',
//   sites: ['sizzlenz'],

// },

{
  key: 'inspiration-feed',
  content: <InspirationFeedSection />,
  sites: ['sizzlenz'],
  // This section renders its own <section> with a white background and internal
  // vertical padding. Match the wrapper to white and drop the extra bottom
  // spacing so the alternating cream background + trailing padding don't leak
  // out as an empty band below the section.
  background: 'bg-white'
  // spacing: 'pb-0',
}, {
  key: 'expert-partners',
  content: <ExpertPartnersSection limit={20} />,
  lazy: true,
  lazyMinHeight: 'clamp(20rem, 30vw, 26rem)'
},
// { key: 'business-marketplace-heading', content: <BusinessMarketplaceHeading /> },
// { key: 'shared-moments', content: <SharedMomentsSection />, background: 'bg-transparent' },
// {
//   key: 'direct-to-right-place',
//   content: <DirectToRightPlaceV4 />,
//   background: 'bg-gray-900',
// },
// {
//   key: 'demand-hero',
//   content: <DemandExportHeroSection />,
//   background: 'bg-gray-950',
//   sites: ['sizzlenz'],
// },
// { key: 'demand', content: <DemandSection />, background: 'bg-white', lazy: true },
// {
//   key: 'export',
//   content: <ExportSection showSpecificScenarios={false} />,
//   background: 'bg-gray-50',
//   lazy: true,
// },
// {
//   key: 'explore-tools',
//   content: <ExploreToolsSection />,
//   lazy: true,
//   lazyMinHeight: 'clamp(32rem, 48vw, 46rem)',

// },
{
  key: 'banner-early-preview',
  isSignInBanner: true,
  content: <SignInEmailBanner heading="Join Sizzle as an Early Insider" subtext="Get practical demand-building tools, ideas, opportunities, and connections for your brand or trade business." buttonLabel="Get Early Access" className="py-10" />
},
// {
//   key: 'explore-hubs',
//   content: <ExploreHubsSection />,
//   lazy: true,
//   lazyMinHeight: 'clamp(30rem, 45vw, 42rem)',
// },
// { key: 'money', content: <MoneySection />, background: 'bg-gray-50', lazy: true },
{
  key: 'try-collaborations',
  content: <HomeCollaborationsSection feedLabel="Hot Examples 👇" />,
  lazy: true,
  lazyMinHeight: 'clamp(28rem, 42vw, 38rem)',
  sites: ['sizzlenz']
},
// {
//   key: 'office-hours',
//   content: <OfficeHoursSection />,
//   lazy: true,
//   lazyMinHeight: 'clamp(30rem, 45vw, 42rem)',
//   sites: ['sizzlenz'],

// },
// {
//   key: 'featured-news',
//   content: <FeaturedNewsSection />,
//   lazy: true,
//   lazyMinHeight: 'clamp(24rem, 36vw, 34rem)',
// },
// { key: 'explore-themes', content: <ExploreThemesSection />, background: 'bg-white', lazy: true },
// {
//   key: 'banner-get-started',
//   isSignInBanner: true,
//   background: 'bg-white',
//   lazy: true,
//   content: (
//     <SignInEmailBanner
//       heading="Ready to get started?"
//       subtext="Whether you're here to discover great products with friends or grow your brand, sign up and start making things happen."
//       buttonLabel="Sign Up"
//       className="py-10"
//     />
//   ),
// },
// { key: 'host-at-your-place', content: <HostAndEventsSection />, background: 'bg-white', lazy: true },
// { key: 'consumer-discover', content: <ConsumerDiscoverSection />, background: 'bg-white', sites: ['sizzlenz'] },
// { key: 'explore-asks', content: <ExploreAsksSection />, background: 'bg-gray-50', lazy: true },
{
  key: 'sizzle-concierge',
  content: <SizzleConciergeSection userSessionRequired={false} />,
  background: 'bg-white',
  spacing: 'pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-24 lg:pb-24'
}, {
  key: 'final-cta',
  content: <FinalCTASectionV3 demandOnly />,
  lazy: true,
  lazyMinHeight: 'clamp(38rem, 58vw, 52rem)'
}
// {
//   key: 'banner-dont-miss-out',
//   isSignInBanner: true,
//   background: 'bg-gray-950',
//   lazy: true,
//   content: (
//     <SignInEmailBanner
//       heading="Sign up now to be one of the first Sizzle Early Insiders"
//       buttonLabel="Sign Up"
//       variant="dark"
//       className="py-12"
//     />
//   ),
// },
];
interface Crosslink {
  context: string;
  label: string;
  href: string;
}

/** Cross-link shown at the bottom of the page pointing users to the other domain. */
const CROSSLINKS: Record<SiteId, Crosslink | null> = {
  sizzlenz: {
    context: 'Based in the US?',
    label: 'Visit Sizzle CPG Global',
    href: 'https://sizzlecpg.com'
  },
  sizzlecpg: {
    context: 'Based in NZ?',
    label: 'Visit Sizzle NZ',
    href: 'https://sizzle.nz'
  },
  // Yummmsy has its own frontend app and does not share this home page.
  yummmsy: null
};
export function HomeMainView() {
  const $ = _c(11);
  const {
    data: session,
    isPending: isSessionPending
  } = useSession();
  const {
    siteId
  } = useSiteSettings();
  const isLoggedIn = !!session?.user;
  let t0;
  if ($[0] !== isLoggedIn || $[1] !== isSessionPending || $[2] !== siteId) {
    t0 = SECTIONS.filter(section => {
      if (section.sites && !section.sites.includes(siteId)) {
        return false;
      }
      if (section.isSignInBanner && (isLoggedIn || isSessionPending)) {
        return false;
      }
      return true;
    });
    $[0] = isLoggedIn;
    $[1] = isSessionPending;
    $[2] = siteId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const visibleSections = t0;
  const crosslink = CROSSLINKS[siteId];
  let t1;
  if ($[4] !== visibleSections) {
    t1 = visibleSections.map(_temp);
    $[4] = visibleSections;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== crosslink) {
    t2 = false && crosslink && <div className="border-t border-orange-100 bg-[#fffaf4] px-4 py-7 text-center"><p className="text-sm text-gray-600">{crosslink.context}{" "}<a href={crosslink.href} target="_blank" rel="noopener noreferrer" className="inline-flex cursor-pointer items-center gap-1 font-semibold text-primary-600 transition-colors hover:text-primary-700">{crosslink.label}<ArrowUpRight className="h-4 w-4" aria-hidden="true" /></a></p></div>;
    $[6] = crosslink;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className="min-h-screen overflow-x-clip bg-white">{t1}{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function _temp(t0, index) {
  const {
    key,
    content,
    background,
    lazy,
    lazyMinHeight,
    spacing
  } = t0;
  const resolvedBackground = background ?? ALTERNATING_BACKGROUNDS[index % ALTERNATING_BACKGROUNDS.length];
  return <div key={key} className={cn(resolvedBackground, spacing ?? DEFAULT_SECTION_SPACING)} data-home-section={key}>{lazy ? <LazySection minHeight={lazyMinHeight}>{content}</LazySection> : content}</div>;
}